import { FC } from 'react';
import Divider from '@mui/material/Divider';
import { Controller } from 'react-hook-form';
import { KycRequestProcess } from 'further-types/lp/kyc';
import { Column } from 'further-ui/components/Layout';
import BasicSelect from 'further-ui/components/BasicSelect';
import Switch from 'further-ui/components/Switch';
import FormField from 'further-ui/components/FormField';
import useKycSettings from './useKycSettings';
import useOnboardingSettings from 'hooks/lp/data/vehicle/useOnboardingSettings';
import BasicCard from '../BasicCard';
import CustomKycRequests from './CustomKycRequests';

type Props = {
  vehicleId: string;
};

const KycSettings: FC<Props> = ({ vehicleId }) => {
  const {
    control,
    kycRequestProcess,
    kycRequestProcessOptions,
    handleKycRequestProcessChange,
  } = useKycSettings(vehicleId);

  const { onboardingSettings, updateOnboardingSettings, isUpdating } =
    useOnboardingSettings(vehicleId);

  return (
    <Column fullWidth spacing="lg">
      <FormField
        spacing="xs"
        label="Automatically address check investors"
        tooltip="Upon an investor completing KYC, the system can check the investor's address against international databases to confirm if they are living there. This service does not work in all countries, but covers the UK, US and some European countries well. There is an additional £2 charge per check for this service."
      >
        <Switch
          checked={onboardingSettings.checkInvestorAddress}
          onChange={(value) => {
            updateOnboardingSettings({ checkInvestorAddress: value });
          }}
          disabled={isUpdating}
        />
      </FormField>

      <Divider />
      <FormField
        spacing="xs"
        label="KYC request process"
        tooltip="Select your individual investor KYC requests. eKYC requires users to complete electronic KYC (single ID and video of user with automatic checks against international AML databases). Information request is used where you wish to request KYC information or documentation after (or instead of) individual investor eKYC."
      >
        <Controller
          control={control}
          name="kycRequestProcess"
          render={({ field }) => (
            <BasicSelect
              options={kycRequestProcessOptions}
              value={field.value}
              onChange={(value) => {
                field.onChange(value);
                handleKycRequestProcessChange(value);
              }}
            />
          )}
        />
      </FormField>

      <Divider />
      {kycRequestProcess === KycRequestProcess.InformationRequestOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <BasicCard
          title="KYC information requests"
          description="Set up KYC onboarding requests required from investors. Create individual requests, group related ones into sections, and adjust their order with drag and drop to align with your review process."
        />
      ) : null}
      {kycRequestProcess === KycRequestProcess.EKycOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <BasicCard
          title="eKYC request"
          description="The system's automated eKYC flow will request an identity document and check the investor against international AML databases globally."
          outlined
          number={
            kycRequestProcess === KycRequestProcess.EKycAndInformationRequest
              ? 1
              : undefined
          }
        />
      ) : null}
      {kycRequestProcess === KycRequestProcess.InformationRequestOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <BasicCard
          title="KYC information"
          description="As a prospective private fund investor, we must first carry out Know Your Customer checks. To do this, please submit your information below."
          outlined
          number={
            kycRequestProcess === KycRequestProcess.EKycAndInformationRequest
              ? 2
              : undefined
          }
        />
      ) : null}
      {kycRequestProcess === KycRequestProcess.InformationRequestOnly ||
      kycRequestProcess === KycRequestProcess.EKycAndInformationRequest ? (
        <>
          <Divider />
          <CustomKycRequests vehicleId={vehicleId} />
        </>
      ) : null}
    </Column>
  );
};

export default KycSettings;
